import { ProductTileModel } from '@notino/react-styleguide';

import {
  AttributeTypes,
  MaximumVolumeInPercent,
  AttributeType,
} from '@containers/ProductListing/model';

import { ILabelAttributes, getLabelAttributes } from './model';

type Label = AttributeTypes | ProductTileModel.ProductLabelAttributes;

/**
 * Returns an object of variant attributes, where keys are attributes (label) and values are its properties.
 * It respects the order according to labelAttributes keys
 */
export const getLabelAttrs = ({
  attributes,
  supportedAttributes,
}: {
  attributes: Label[];
  supportedAttributes: string[];
}): Partial<ILabelAttributes> => {
  const resolvedLabelAttributes = getLabelAttributes();
  return Object.keys(resolvedLabelAttributes)
    .map((label) => attributes.find((variantLabel) => variantLabel === label))
    .filter((element): element is Label => element !== undefined)
    .filter((label) => supportedAttributes.includes(label))
    .reduce((acc, label) => {
      acc[label] = resolvedLabelAttributes[label];
      return acc;
    }, {});
};

export const resolveDamageAttribute = (attributeValue: {
  volumeInPercent: number;
}) => {
  return attributeValue.volumeInPercent === MaximumVolumeInPercent
    ? ProductTileModel.ProductLabelAttributes.Damaged
    : ProductTileModel.ProductLabelAttributes.Returned;
};

const mapAttrsToProductLabelAttrs = (
  label: AttributeTypes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributeValue: any
): ProductTileModel.ProductLabelAttributes => {
  if (label === AttributeTypes.Damage) {
    return resolveDamageAttribute(attributeValue);
  }
  return label as unknown as ProductTileModel.ProductLabelAttributes;
};

export const orderTags = (tags: ProductTileModel.ProductLabelAttributes[]) => {
  const tagsOrder = [
    ProductTileModel.ProductLabelAttributes.BargainSet,
    ProductTileModel.ProductLabelAttributes.Action,
    ProductTileModel.ProductLabelAttributes.Clearance,
    ProductTileModel.ProductLabelAttributes.FreeDelivery,
    ProductTileModel.ProductLabelAttributes.VoucherDiscount,
    ProductTileModel.ProductLabelAttributes.ConditionalVoucherDiscount,
    ProductTileModel.ProductLabelAttributes.Gift,
    ProductTileModel.ProductLabelAttributes.Exclusive,
    ProductTileModel.ProductLabelAttributes.Returned,
    ProductTileModel.ProductLabelAttributes.Damaged,
    ProductTileModel.ProductLabelAttributes.New,
    ProductTileModel.ProductLabelAttributes.VirtualTryOn,
  ];

  return [...tags].sort((a, b) => {
    const indexA = tagsOrder.indexOf(a);
    const indexB = tagsOrder.indexOf(b);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    } else if (indexA !== -1) {
      return -1;
    } else if (indexB !== -1) {
      return 1;
    }
    return 0;
  });
};

export const transformAttributesToTags = (
  attributes: Record<string, unknown> = {}
): ProductTileModel.ProductLabelAttributes[] => {
  const tags = Object.keys(attributes)
    .filter((key) => Boolean(attributes[key]))
    .map((key: AttributeTypes) => {
      const label = mapAttrsToProductLabelAttrs(key, attributes[key]);
      return ProductTileModel.ProductLabelAttributes[label];
    })
    .filter(Boolean);

  return orderTags(tags);
};

const addProductLabelAttributes = (attributes: AttributeType): Label[] =>
  Object.keys(attributes).reduce(
    (acc, label) => [
      ...acc,
      mapAttrsToProductLabelAttrs(label as AttributeTypes, attributes[label]),
    ],
    []
  );

export const getRenderableAttributes = ({
  attributes,
  supportedAttributes,
}: {
  attributes: AttributeType;
  supportedAttributes: string[];
}): Partial<ILabelAttributes> =>
  getLabelAttrs({
    attributes: addProductLabelAttributes(attributes),
    supportedAttributes,
  });
